import type { IPortfolioCodeQualitySummary } from '@skystone/shared';
import { EMPTY_CODE_QUALITY_AGGREGATE } from '@skystone/shared';
import { MedianCoverageTile } from './MedianCoverageTile';
import { QgPassRateTile } from './QgPassRateTile';
import { ReliabilityGradeMixTile } from './ReliabilityGradeMixTile';

interface VerdictStripSlotProps {
  /** When undefined: still loading (skeleton). When null: load failed.
   *  When populated: render the three tiles from `summary.aggregate`. */
  summary: IPortfolioCodeQualitySummary | null | undefined;
}

/**
 * Verdict-strip — three tiles at the top of the Code Quality tab:
 *   QG pass rate, median coverage, reliability grade mix.
 *
 * US3 (PRODOPSAI-331) wires the real content. Tiles render from
 * `summary.aggregate`; when summary is undefined (still loading) or null
 * (fetch failed), the tiles render with the empty aggregate so the layout
 * stays stable and the user sees "—" / "No analysed repos" copy rather than
 * a flash of placeholder text.
 *
 * Per epic D18: stacked 1×3 at `base`, 3-col at `sm+`. The grid wrapper is
 * frozen by the shell layout — this component only renders the tiles.
 */
export function VerdictStripSlot({ summary }: Readonly<VerdictStripSlotProps>) {
  const aggregate = summary?.aggregate ?? EMPTY_CODE_QUALITY_AGGREGATE;
  const inScopeRepos = summary?.inScopeRepos ?? 0;

  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="code-quality-verdict-strip">
      <QgPassRateTile aggregate={aggregate} />
      <MedianCoverageTile aggregate={aggregate} inScopeRepos={inScopeRepos} />
      <ReliabilityGradeMixTile aggregate={aggregate} inScopeRepos={inScopeRepos} />
    </div>
  );
}
